import React, { useEffect, useState } from 'react';

import {
  faUser,
  faPhone,
  faTag,
  faPlug,
  faBellSlash
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Paper,
  Table,
  Badge,
  Pagination,
  Group,
  Text,
  LoadingOverlay,
  Alert,
  Title,
  Button
} from '@mantine/core';
import { Link } from 'react-router-dom';

import { type Caller } from './types';
import { FormatPhone } from '../../utils/format';
import {
  SYSTEM_CONFIGS,
  useActiveIntegrations
} from '../../utils/integration';
import { usePostJSON } from '../../utils/request';

interface CallerResponse {
  success: boolean;
  callers: Caller[];
  total: number;
  page: number;
  limit: number;
}

interface CallerListProps {
  title: string;
  search?: string;
}

const CallerList: React.FC<CallerListProps> = ({ title, search }) => {
  const [page, setPage] = useState(1);
  const [limit] = useState(10);

  const activeInts = useActiveIntegrations();

  // Reset to first page when search changes
  useEffect(() => {
    setPage(1);
  }, [search]);

  const {
    data,
    error,
    loading: isLoading,
    refetch
  } = usePostJSON<CallerResponse>(
    '/api/caller/data',
    { page, limit, search: search || undefined },
    [page, limit, search]
  );

  useEffect(() => {
    // ensure refetch is stable when dependencies change
    void refetch();
  }, [refetch]);

  const totalPages = Math.ceil((data?.total || 0) / limit);
  const callers = data?.callers || [];
  const total = data?.total || 0;
  const hasActiveIntegrations = Object.keys(activeInts).length > 0;

  if (error) {
    return (
      <Paper withBorder p="md" style={{ position: 'relative' }}>
        <Alert color="red" title="Error">
          {error.message}
        </Alert>
      </Paper>
    );
  }

  return (
    <Paper withBorder p="md" style={{ position: 'relative' }}>
      <LoadingOverlay visible={isLoading} />

      <Group justify="space-between" mb="md">
        <Title order={3}>{title}</Title>
        <Badge color="blue" variant="light">
          {total} {total === 1 ? 'caller' : 'callers'}
        </Badge>
      </Group>

      {callers.length === 0 ? (
        <Text c="dimmed" py="xl" ta="center">
          No callers found.
        </Text>
      ) : (
        <>
          <Table highlightOnHover striped layout="fixed">
            <Table.Thead>
              <Table.Tr>
                <Table.Th w={hasActiveIntegrations ? "25%" : "35%"}>Name</Table.Th>
                <Table.Th w={hasActiveIntegrations ? "20%" : "25%"}>Phone</Table.Th>
                {hasActiveIntegrations && (
                  <Table.Th w="20%">Integration</Table.Th>
                )}
                <Table.Th w={hasActiveIntegrations ? "15%" : "20%"}>Type</Table.Th>
                <Table.Th w={hasActiveIntegrations ? "20%" : "20%"}>
                  Actions
                </Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {callers.map((caller) => (
                <Table.Tr key={caller.id}>
                  <Table.Td style={{ maxWidth: 0 }}>
                    {(caller.firstName || caller.lastName) ? (
                      <Group gap="xs" wrap="nowrap">
                        <FontAwesomeIcon icon={faUser} size="sm" style={{ flexShrink: 0 }} />
                        <Text fw={500} title={`${caller.firstName ?? ''} ${caller.lastName ?? ''}`.trim()} truncate>
                          {`${caller.firstName ?? ''} ${caller.lastName ?? ''}`.trim()}
                        </Text>
                      </Group>
                    ) : (
                      <Text c="dimmed" size="sm">Unknown</Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs">
                      <FontAwesomeIcon icon={faPhone} size="sm" />
                      <Text size="sm">{FormatPhone(caller.phone)}</Text>
                    </Group>
                  </Table.Td>
                  {hasActiveIntegrations && (
                    <Table.Td>
                      {caller.externalSystem && caller.externalId ? (() => {
                        const systemConfig = SYSTEM_CONFIGS[caller.externalSystem];
                        const integration = activeInts[caller.externalSystem];
                        const linkUrl = systemConfig?.utils?.getCallerLink && integration?.metadata && caller.externalId
                          ? systemConfig.utils.getCallerLink(integration.metadata as Record<string, string>, caller.externalId)
                          : null;
                        const displayName = systemConfig?.name || caller.externalSystem;
                        
                        return (
                          <Group gap="xs">
                            <FontAwesomeIcon icon={faPlug} size="sm" />
                            {linkUrl ? (
                              <Text
                                component="a"
                                href={linkUrl}
                                rel="noopener noreferrer"
                                size="sm"
                                style={{ cursor: 'pointer', textDecoration: 'underline' }}
                                target="_blank"
                              >
                                {displayName}
                              </Text>
                            ) : (
                              <Text size="sm">
                                {displayName}
                              </Text>
                            )}
                          </Group>
                        );
                      })() : (
                        <Text c="dimmed" size="sm">Not Linked</Text>
                      )}
                    </Table.Td>
                  )}
                  <Table.Td>
                    <Group gap="xs">
                      {caller.type ? (
                        <Badge
                          color={
                            caller.type.toLowerCase() === 'aide'
                              ? 'blue'
                              : caller.type.toLowerCase() === 'family'
                              ? 'green'
                              : 'gray'
                          }
                          leftSection={<FontAwesomeIcon icon={faTag} size="xs" />}
                          variant="light"
                        >
                          {caller.type}
                        </Badge>
                      ) : (
                        <Text c="dimmed" size="sm">—</Text>
                      )}
                      {caller.isOptedOut && (
                        <Badge
                          color="orange"
                          leftSection={<FontAwesomeIcon icon={faBellSlash} size="xs" />}
                          variant="light"
                        >
                          Opted out
                        </Badge>
                      )}
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs" justify="flex-start">
                      <Button
                        component={Link}
                        size="xs"
                        to={`/caller/${caller.id}/encounters`}
                        variant="light"
                      >
                        Encounters
                      </Button>
                      <Button
                        component={Link}
                        size="xs"
                        to={`/caller/edit/${caller.id}`}
                        variant="light"
                      >
                        Edit
                      </Button>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>

          {totalPages > 1 && (
            <Group justify="center" mt="md">
              <Pagination
                total={totalPages}
                value={page}
                onChange={setPage}
              />
            </Group>
          )}
        </>
      )}
    </Paper>
  );
};

export default CallerList;


